import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePostHogIdentification } from '@/hooks/usePostHogIdentification';
import { isSurveyDue, markSurveyShown, TASK_DONE_EVENT, TASK_SATISFACTION_SURVEY_KEY } from '@/helpers/surveyStorage';
import styles from './AdoptionSurveys.module.scss';

export default function TaskSatisfactionPrompt() {
  const { t } = useTranslation();
  const { captureEvent } = usePostHogIdentification();
  const [task, setTask] = useState<string | null>(null);
  const [comment, setComment] = useState('');

  useEffect(() => {
    const onDone = (event: Event) => {
      const custom = event as CustomEvent<{ task?: string }>;
      if (!custom.detail?.task) return;
      if (!isSurveyDue(TASK_SATISFACTION_SURVEY_KEY)) return;
      setTask(custom.detail.task);
    };
    window.addEventListener(TASK_DONE_EVENT, onDone);
    return () => window.removeEventListener(TASK_DONE_EVENT, onDone);
  }, []);

  if (!task) return null;

  const close = (score?: number) => {
    markSurveyShown(TASK_SATISFACTION_SURVEY_KEY);
    captureEvent('task_satisfaction', {
      task,
      score,
      comment: comment.trim() || undefined,
      skipped: score == null,
    });
    setTask(null);
    setComment('');
  };

  return (
    <div className={styles.toast} role="dialog" aria-label={t('taskSatisfaction.title')}>
      <p className="body-lr-semibold">{t('taskSatisfaction.title')}</p>
      <p className="body-md-regular text-(--font-sub-title)">{t(`taskSatisfaction.tasks.${task}`)}</p>
      <div className={styles.stars}>
        {[1, 2, 3, 4, 5].map((score) => (
          <button key={score} type="button" className={styles.star} onClick={() => close(score)} aria-label={`${score}`}>
            {score}
          </button>
        ))}
      </div>
      <textarea
        className={styles.detail}
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        placeholder={t('taskSatisfaction.commentPlaceholder')}
        rows={2}
      />
      <button type="button" className={styles.secondary} onClick={() => close()}>
        {t('taskSatisfaction.skip')}
      </button>
    </div>
  );
}
